import { getCookie, setCookie, deleteCookie } from '../utils/dom';
import { deepClone } from '../utils/object';
import { unique } from '../utils/array';
import { shippingMethodsMapping } from '../config';
import {
	VOUCHER_INPUT,
	VOUCHER_ERROR,
	RECENTLY_VIEWED,
	SELECT_SIZE,
	ADD_TO_BAG_ERROR,
	UPDATE_SELECTION_CACHE,
	UPDATE_GIFT_CARD_AMOUNT,
	UPDATE_SELECTION,
	UPDATE_ADDITIONAL_INFORMATION,
	GET_ADDRESS,
	UPDATE_ADDRESS,
	UPDATE_ERRORS,
	DELIVERY_PROCEED,
	KLARNA_LOADED,
	KLARNA_UNLOADED,
	INGRID_LOADED,
	INGRID_UNLOADED,
	ADYEN_INITIATED,
	STRIPE_INITIATED,
	STRIPE_PAYMENT_INTENTS_INITIATED,
	PAYMENT_UPDATING,
	SELECTION_ERROR,
	SELECTION_ERROR_PASSWORD,
	SELECTION_ERROR_REGISTERED,
	SELECTION_ERROR_STOCK,
	ENRICH_PRODUCTS,
	UPDATE_BUDBEE,
	INITIATED_SCRIPT,
	PAYMENT_RETURN,
	BOOLEAN_CHANGE,
	DESTROY_SELECTION,
} from '../actions/checkout';

let token = null;
let giftCardToken = null;
if (!token && typeof document !== 'undefined') token = getCookie('selection');
if (!giftCardToken && typeof document !== 'undefined') giftCardToken = getCookie('giftCardSelection');

const initialState = {
	items: {},
	selectionCache: {},
	recentlyViewed: [],
	useShippingAddress: false,
	initiatedScript: false,
	vouchers: {
		currentValue: '',
		error: false,
	},
	addressRetrieved: {
		successful: false,
		error: false,
	},
	account: {
		loggedIn: false,
	},
	giftCard: {
		type: null,
		amount: 0,
		continue: false,
	},
	payment: {
		address: {},
		shippingAddress: {},
		paymentFailedPage: '',
		paymentReturnPage: '',
		termsAndConditions: false,
		additionalInformation: [], // [{ item: xx-xx, note: 'Product: X — Personalization: H.B.C' }, { item: xx-xx, note: 'Product: X — Length: 31 — Turn Ups: no' }]
	},
	enrichProducts: {
		personalization: '',
		length: null,
		turnUps: null,
	},
	shippingAvailable: {},
	deliveryProceed: false,
	klarnaLoaded: false,
	klarnaUnloaded: false,
	ingridExists: false,
	ingridLoaded: false,
	ingridUnloaded: false,
	paymentReturn: {},
	addToBagError: false,
	selectionError: false,
	selectionErrorPassword: false,
	selectionErrorRegistered: false,
	selectionErrorStock: false,
	selection: {},
	giftCardSelection: {},
	destroySelection: false,
};

if (token) initialState.selection.token = token;
if (giftCardToken) initialState.giftCardSelection.token = giftCardToken;
if (shippingMethodsMapping.budbee) initialState.shippingAvailable[shippingMethodsMapping.budbee] = false;

const savePaymentInfo = (paymentInfo) => {
	if (typeof localStorage !== 'undefined') {
		// Remove sensitive and duplicate data
		const tempPaymentInfo = deepClone(paymentInfo);

		delete tempPaymentInfo.address.emailOld;
		delete tempPaymentInfo.address.emailNew;
		delete tempPaymentInfo.address.emailRepeat;
		delete tempPaymentInfo.address.emailNewRepeat;
		delete tempPaymentInfo.address.password;
		delete tempPaymentInfo.address.passwordNew;
		delete tempPaymentInfo.address.passwordRepeat;
		delete tempPaymentInfo.address.passwordNewRepeat;
		delete tempPaymentInfo.address.passwordOld;
		tempPaymentInfo.createAccount = false;

		localStorage.setItem('paymentInfo', JSON.stringify(tempPaymentInfo));
	}
};

const checkout = (state = initialState, action = {}) => {
	switch (action.type) {
		case SELECT_SIZE: {
			return {
				...state,
				selectionError: false,
				selectionErrorPassword: false,
				selectionErrorRegistered: false,
				selectionErrorStock: false,
				addToBagError: false,
				items: {
					...state.items,
					[action.product]: {
						...state.items[action.product],
						size: action.item,
						quantity: action.quantity,
					},
				},
			};
		}

		case ADD_TO_BAG_ERROR: {
			return {
				...state,
				addToBagError: true,
			};
		}

		case 'CLEAR_ERRORS': {
			return {
				...state,
				addToBagError: false,
				selectionError: false,
				selectionErrorPassword: false,
				selectionErrorRegistered: false,
				selectionErrorStock: false,
				adyenInitiated: false,
			};
		}

		case RECENTLY_VIEWED: {
			let recentlyViewed = typeof localStorage !== 'undefined' ? JSON.parse(localStorage.getItem('recentlyViewed')) : [];
			recentlyViewed = recentlyViewed || [];
			const productExistsIndex = recentlyViewed.indexOf(action.product);

			if (productExistsIndex === -1) {
				recentlyViewed = [action.product, ...recentlyViewed.slice(0)].slice(0, 4);
			} else {
				recentlyViewed = [action.product, ...recentlyViewed.slice(0, productExistsIndex), ...recentlyViewed.slice(productExistsIndex + 1)].slice(0, 4);
			}

			if (typeof localStorage !== 'undefined' && recentlyViewed.length > 0) localStorage.setItem('recentlyViewed', JSON.stringify(recentlyViewed));
			return {
				...state,
				recentlyViewed,
			};
		}

		case 'INIT_SELECTION_CACHE': {
			return {
				...state,
				selectionCache: action.initCache,
			};
		}

		case UPDATE_SELECTION_CACHE: {
			let updatedCache;

			if (!action.productCache && state.selectionCache[action.product]) {
				// Remove from cache
				updatedCache = Object.keys(state.selectionCache).reduce((acc, obj) => (obj !== action.product ? { ...acc, [obj]: state.selectionCache[obj] } : { ...acc }), {});
			} else if (Array.isArray(action.product)) {
				// Update all in selectionCache
				updatedCache = Object.keys(action.productCache).reduce((acc, product) => {
					const currentProductCache = action.selectionCache[product].generatedClientSide ? action.selectionCache[product] : action.productCache[product];
					return (currentProductCache ? { ...acc, [product]: { ...currentProductCache, category: (action.selectionCache[product].category ? action.selectionCache[product].category : action.productCache[product].canonicalUri) } } : acc);
				}, {});
			} else {
				// Add to cache
				updatedCache = {
					...state.selectionCache,
					[action.product]: action.productCache,
				};
			}

			localStorage.setItem('selectionCache', JSON.stringify(updatedCache));

			return {
				...state,
				selectionCache: updatedCache,
			};
		}

		case UPDATE_GIFT_CARD_AMOUNT: {
			return {
				...state,
				giftCard: {
					...state.giftCard,
					type: action.giftCard,
					amount: action.amount,
					proceed: state.giftCard.proceed === action.amount,
				},
				selectionError: false,
			};
		}

		case 'INITIATE_SELECTION': {
			return {
				...state,
				paymentReturn: {
					...state.paymentReturn,
					paymentHTML: action.formHtml,
				},
			};
		}

		case UPDATE_SELECTION: {
			const selectionToUpdate = action.giftCardSelection || 'selection';
			if (action.giftCardSelection && action.failed) {
				return {
					...state,
					selectionError: true,
				};
			}

			// Why does it update if there is no selection ?
			if (!action.data.selection) return { ...state };

			setCookie(selectionToUpdate, action.data.token, 365);

			return {
				...state,
				giftCard: {
					...state.giftCard,
					...(
						selectionToUpdate === 'giftCardSelection' ? (
							{
								type: (action.data.selection.items.length > 0 && action.data.selection.items[0].item) || state.giftCard.type,
								amount: (action.data.selection.items.length > 0 && action.data.selection.items[0].priceEachAsNumber) || state.giftCard.amount,
								proceed: true,
							}
						) : {}
					),
				},
				account: {
					...state.account,
					loggedIn: action.data.loggedIn || false,
				},
				consents: [/*
					{
						key: "favourites",
						consented: true,
						name: "Favourites 2022-06-20",
						text: "35291,30069,26430,26431",
						version: "1.0",
						language: "en",
					},
				*/],
				selectionError: false,
				selectionErrorStock: false,
				addToBagError: false,
				[selectionToUpdate]: { // Replace all data every time.
					token: action.data.token,
					paymentMethod: action.data.selection.paymentMethod,
					shippingMethod: action.data.selection.shippingMethod,
					items: action.data.selection.items.map((item) => ({
						item: item.item,
						size: item.size,
						...(
							item.product ? {
								name: item.product.name,
								id: `p${item.product.centraProduct}-v${item.product.centraVariant}`, // Match google merchant feed.
								sku: item.product.productSku,
								brand: item.product.brandName,
								variant: item.product.variantName,
								category: item.product.categoryUri,
							} : {}
						),
						quantity: item.quantity,
						line: item.line,
						priceEach: item.priceEach,
						priceEachAsNumber: item.priceEachAsNumber,
						totalPrice: item.totalPrice,
						totalPriceAsNumber: item.totalPriceAsNumber,
						priceEachBeforeDiscount: item.priceEachBeforeDiscount,
						priceEachBeforeDiscountAsNumber: item.priceEachBeforeDiscountAsNumber,
						anyDiscount: item.anyDiscount,
						taxPercent: item.taxPercent,
						priceEachWithoutTax: item.priceEachWithoutTax,
						priceEachWithoutTaxAsNumber: item.priceEachWithoutTaxAsNumber,
						priceEachReduction: item.priceEachReduction,
						priceEachReductionAsNumber: item.priceEachReductionAsNumber,
					})),
					discounts: deepClone(action.data.selection.discounts),
					totals: { ...action.data.selection.totals },
					paymentMethods: action.data.paymentMethods.map((method) => ({ ...method })),
					shippingMethods: action.data.shippingMethods.map((method) => ({ ...method })),
					paymentFields: deepClone(action.data.paymentFields),
				},
			};
		}

		case UPDATE_ADDITIONAL_INFORMATION: {
			// Send empty note to delete from additional information.
			const exists = state.payment.additionalInformation.reduce((acc, item, i) => (item.item === action.note.item ? i : acc), -1);
			let additionalInformation = [];

			if (exists === -1 && action.note && action.note.message !== '') {
				// Add
				additionalInformation = [...state.payment.additionalInformation, { ...action.note }];
			} else if (!action.note || action.note.message === '') {
				// Remove
				additionalInformation = [...state.payment.additionalInformation.slice(0, exists), ...state.payment.additionalInformation.slice(exists + 1)];
			} else {
				// Update
				additionalInformation = [...state.payment.additionalInformation.slice(0, exists), { ...action.note }, ...state.payment.additionalInformation.slice(exists + 1)];
			}

			const paymentInfo = {
				...state.payment,
				additionalInformation,
			};

			savePaymentInfo(paymentInfo);

			return {
				...state,
				payment: paymentInfo,
			};
		}

		case 'INIT_PAYMENT_INFO': {
			return {
				...state,
				payment: {
					...state.payment,
					...action.paymentInfo,
				},
			};
		}

		case DELIVERY_PROCEED: {
			return {
				...state,
				deliveryProceed: true,
			};
		}

		case KLARNA_UNLOADED: {
			return {
				...state,
				klarnaUnloaded: true,
			};
		}

		case KLARNA_LOADED: {
			return {
				...state,
				klarnaLoaded: true,
			};
		}

		case INGRID_UNLOADED: {
			return {
				...state,
				ingridUnloaded: true,
			};
		}

		case INGRID_LOADED: {
			return {
				...state,
				ingridLoaded: true,
			};
		}

		case ADYEN_INITIATED: {
			return {
				...state,
				adyenInitiated: true,
			};
		}

		case STRIPE_INITIATED: {
			return {
				...state,
				stripeInitiated: true,
			};
		}

		case STRIPE_PAYMENT_INTENTS_INITIATED: {
			return {
				...state,
				stripePaymentIntentsInitiated: true,
			};
		}

		case PAYMENT_UPDATING: {
			return {
				...state,
				paymentUpdating: action.isUpdating,
			};
		}

		case UPDATE_ADDRESS: {
			const paymentInfo = action.group ? {
				...state.payment,
				[action.group]: {
					...state.payment[action.group],
					[action.id]: action.value,
				},
			} : {
				...state.payment,
				[action.id]: action.value,
			};

			savePaymentInfo(paymentInfo);

			return {
				...state,
				deliveryProceed: !((action.id === 'zipCode' && state.deliveryProceed && state.payment[action.group][action.id] !== action.value) || !state.deliveryProceed),
				selectionError: false,
				selectionErrorStock: false,
				payment: paymentInfo,
			};
		}

		case UPDATE_ERRORS: {
			const selectionToUpdate = action.giftCardSelection || 'selection';

			if (action.group) {
				const field = action.group === 'shippingAddress' ? state[selectionToUpdate].paymentFields.address[action.id] : state[selectionToUpdate].paymentFields[action.group][action.id];
				if (action.id === 'state') field.required = true;

				return {
					...state,
					[selectionToUpdate]: {
						...state[selectionToUpdate],
						paymentFields: {
							...state[selectionToUpdate].paymentFields,
							[action.group]: {
								...state[selectionToUpdate].paymentFields[action.group],
								[action.id]: {
									...state[selectionToUpdate].paymentFields[action.group][action.id],
									error: field.visible && field.required ? action.error : false,
								},
							},
						},
					},
				};
			}
			if (state[selectionToUpdate].paymentFields[action.id]) {
				const singleField = state[selectionToUpdate].paymentFields[action.id];
				return {
					...state,
					[selectionToUpdate]: {
						...state[selectionToUpdate],
						paymentFields: {
							...state[selectionToUpdate].paymentFields,
							[action.id]: {
								...state[selectionToUpdate].paymentFields[action.id],
								error: singleField.visible && singleField.required ? action.error : false,
							},
						},
					},
				};
			}
			return { ...state };
		}

		case SELECTION_ERROR: {
			return {
				...state,
				selectionError: action.hasError,
			};
		}

		case SELECTION_ERROR_PASSWORD: {
			return {
				...state,
				selectionErrorPassword: action.hasError,
			};
		}

		case SELECTION_ERROR_REGISTERED: {
			return {
				...state,
				selectionErrorRegistered: action.hasError,
			};
		}

		case SELECTION_ERROR_STOCK: {
			return {
				...state,
				selectionErrorStock: action.hasError,
			};
		}

		case ENRICH_PRODUCTS: {
			return {
				...state,
				enrichProducts: {
					...state.enrichProducts,
					...action,
				},
			};
		}

		case GET_ADDRESS: {
			if (!action.success) {
				return {
					...state,
					addressRetrieved: {
						...state.addressRetrieved,
						error: true,
					},
				};
			}
			return {
				...state,
				addressRetrieved: {
					...state.addressRetrieved,
					successful: true,
					error: false,
				},
				payment: {
					...state.payment,
					address: {
						...state.payment.address,
						...action.data.address,
					},
				},
			};
		}

		case UPDATE_BUDBEE: {
			return {
				...state,
				shippingAvailable: {
					...state.shippingAvailable,
					[shippingMethodsMapping.budbee]: action.success,
				},
			};
		}

		case BOOLEAN_CHANGE: {
			if (action.id === 'useShipping') {
				return {
					...state,
					selectionError: false,
					selectionErrorPassword: false,
					selectionErrorRegistered: false,
					selectionErrorStock: false,
					useShippingAddress: !state.useShippingAddress,
					payment: {
						...state.payment,
						shippingAddress: {
							...state.payment.shippingAddress,
							email: state.payment.shippingAddress.email || state.payment.address.email,
							firstName: state.payment.shippingAddress.firstName || state.payment.address.firstName,
							lastName: state.payment.shippingAddress.lastName || state.payment.address.lastName,
							company: state.payment.shippingAddress.company || state.payment.address.company,
							phoneNumber: state.payment.shippingAddress.phoneNumber || state.payment.address.phoneNumber,
							address1: state.payment.shippingAddress.address1 || state.payment.address.address1,
							address2: state.payment.shippingAddress.address2 || state.payment.address.address2,
							zipCode: state.payment.shippingAddress.zipCode || state.payment.address.zipCode,
							city: state.payment.shippingAddress.city || state.payment.address.city,
							country: state.payment.shippingAddress.country || state.payment.address.country,
						},
					},
				};
			}
			if (action.id === 'subscribeToNewsletter') {
				return {
					...state,
					selectionError: false,
					selectionErrorPassword: false,
					selectionErrorRegistered: false,
					selectionErrorStock: false,
					payment: {
						...state.payment,
						address: {
							...state.payment.address,
							newsletter: !state.payment.address.newsletter,
						},
					},
				};
			}

			const paymentInfo = {
				...state.payment,
				[action.id]: !state.payment[action.id],
			};

			savePaymentInfo(paymentInfo);

			return {
				...state,
				selectionError: false,
				selectionErrorPassword: false,
				selectionErrorRegistered: false,
				selectionErrorStock: false,
				payment: paymentInfo,
			};
		}

		case VOUCHER_INPUT: {
			return {
				...state,
				selectionError: false,
				selectionErrorPassword: false,
				selectionErrorRegistered: false,
				selectionErrorStock: false,
				vouchers: {
					...state.vouchers,
					currentValue: action.value,
					error: false,
				},
			};
		}

		case VOUCHER_ERROR: {
			return {
				...state,
				vouchers: {
					...state.vouchers,
					error: !state.vouchers.error,
				},
			};
		}

		case PAYMENT_RETURN: {
			if (window.CentraCheckout) window.CentraCheckout.resume();
			if (!action.data) {
				return {
					...state,
					paymentReturn: {},
				};
			}

			const nextState = {
				...state,
				paymentReturn: {
					paymentHTML: action.data.selection?.pluginFields?.paymentHTML || state.paymentReturn.paymentHTML || action.data.selection?.paymentHTML || '',
					shippingHTML: action.data.selection?.pluginFields?.shipwallet?.snippet,
					centraCheckoutScript: action.data.selection?.centraCheckoutScript,
				},
			};

			if (!nextState.ingridExists && !!action.data.selection?.pluginFields?.shipwallet?.snippet) nextState.ingridExists = true;

			return nextState;
		}

		case 'ACCOUNT_FORGOT_SENT': {
			return {
				...state,
				selectionSuccess: {
					forgotPasswordSuccess: true,
				},
			};
		}

		case 'ACCOUNT_FORGOT_FAILED': {
			delete state.selectionSuccess;

			return {
				...state,
				selectionErrors: {
					...state.selectionErrors,
					forgotPasswordError: action.hasError,
				},
			};
		}

		// case 'ACCOUNT_ENTRY_FAILED':
		case 'TOGGLE_LOGIN': {
			return {
				...state,
				checkoutLogin: !state.checkoutLogin,
			};
		}

		case 'TOGGLE_ACCOUNT_ENTRY': {
			let accountEntry = 'login';
			if (state.accountEntry === 'register' && action.state === 'registerLogin') accountEntry = 'login';
			else if (state.accountEntry === 'login' && action.state === 'registerLogin') accountEntry = 'register';
			else if (state.accountEntry === 'forgot' && action.state === 'loginForgot') accountEntry = 'login';
			else if (state.accountEntry === 'login' && action.state === 'loginForgot') accountEntry = 'forgot';
			else accountEntry = action.state;

			delete state.selectionSuccess;

			return {
				...state,
				selectionErrors: {},
				formErrors: {},
				accountEntry,
			};
		}

		case 'UPDATE_ACCOUNT': {
			const paymentInfo = {
				...state.payment,
				createAccount: false,
				address: {
					...state.payment.address,
					newsletter: action.data.loggedIn.newsletter || state.payment.address.newsletter,
					email: action.data.loggedIn.email || state.payment.address.email,
					firstName: action.data.loggedIn.firstName || state.payment.address.firstName,
					lastName: action.data.loggedIn.lastName || state.payment.address.lastName,
					company: action.data.loggedIn.company || state.payment.address.company,
					phoneNumber: action.data.loggedIn.phoneNumber || state.payment.address.phoneNumber,
					address1: action.data.loggedIn.address1 || state.payment.address.address1,
					address2: action.data.loggedIn.address2 || state.payment.address.address2,
					zipCode: action.data.loggedIn.zipCode || state.payment.address.zipCode,
					city: action.data.loggedIn.city || state.payment.address.city,
					state: action.data.loggedIn.state || state.payment.address.state,
					country: action.data.loggedIn.country || state.payment.address.country,
					emailOld: '',
					emailNew: '',
					emailRepeat: '',
					emailNewRepeat: '',
					password: '',
					passwordNew: '',
					passwordRepeat: '',
					passwordNewRepeat: '',
					passwordOld: '',
				},
			};

			savePaymentInfo(paymentInfo);
			setCookie('selection', action.data.token, 365);

			return {
				...state,
				payment: paymentInfo,
				selectionErrorPassword: false,
				selectionErrorRegistered: false,
				selection: {
					...state.selection,
					token: action.data.token || '',
				},
				account: {
					...state.account,
					loggedIn: true,
				},
			};
		}

		case 'ACCOUNT_ORDERS': {
			const orders = [
				...(state.account.orders || []),
				...deepClone(action.data.orders),
			].reduce((acc, o) => {
				if (acc.length === 0) return [o];
				// Order doesn't exist in array add.
				if (acc.filter((fo) => fo.order === o.order).length === 0) return [...acc, o];
				// Order exists in array skip.
				return acc;
			}, []);


			return {
				...state,
				account: {
					...state.account,
					orders,
					hasMoreOrders: action.data.ordersPaging.totalSize > orders.length,
				},
			};
		}

		case 'ACCOUNT_LOGOUT': {
			return {
				...state,
				account: {
					...state.account,
					loggedIn: false,
				},
			};
		}

		case INITIATED_SCRIPT: {
			return {
				...state,
				initiatedScript: true,
			};
		}

		case 'RECEIPT_DATA': {
			const selectionToUpdate = action.giftCardSelection || 'selection';
			let receiptSnippet = false;
			if (action.data.order.paymentMethodData && action.data.order.paymentMethodData.snippet) {
				receiptSnippet = action.data.order.paymentMethodData.snippet;
			}

			return {
				...state,
				selectionError: false,
				selectionErrorPassword: false,
				selectionErrorRegistered: false,
				selectionErrorStock: false,
				[selectionToUpdate]: { // Replace all data every time.
					token: action.data.token,
					order: action.data.order.order,
					currency: action.data.order.currency,
					date: action.data.order.date,
					items: action.data.order.items.map((item) => ({
						item: item.item,
						size: item.size,
						...(
							item.product ? {
								name: item.product.name,
								id: `p${item.product.centraProduct}-v${item.product.centraVariant}`, // Match google merchant feed.
								sku: item.product.productSku,
								brand: item.product.brandName,
								variant: item.product.variantName,
								category: item.product.categoryUri,
							} : {}
						),
						quantity: item.quantity,
						line: item.line,
						priceEach: item.priceEach,
						priceEachAsNumber: item.priceEachAsNumber,
						totalPrice: item.totalPrice,
						totalPriceAsNumber: item.totalPriceAsNumber,
						priceEachBeforeDiscount: item.priceEachBeforeDiscount,
						priceEachBeforeDiscountAsNumber: item.priceEachBeforeDiscountAsNumber,
						anyDiscount: item.anyDiscount,
						taxPercent: item.taxPercent,
						priceEachWithoutTax: item.priceEachWithoutTax,
						priceEachWithoutTaxAsNumber: item.priceEachWithoutTaxAsNumber,
						priceEachReduction: item.priceEachReduction,
						priceEachReductionAsNumber: item.priceEachReductionAsNumber,
					})),
					discounts: deepClone(action.data.order.discounts),
					totals: { ...action.data.order.totals },
					address: { ...action.data.order.address },
					shippingAddress: { ...action.data.order.shippingAddress },
					receiptSnippet,
				},
			};
		}

		case 'RECEIPT_ERROR': {
			return {
				...state,
				selectionError: action.reason,
			};
		}

		case DESTROY_SELECTION: {
			const selectionToUpdate = action.giftCardSelection || 'selection';
			const paymentInfo = {
				...state.payment,
				termsAndConditions: false,
			};

			if (selectionToUpdate === 'selection') {
				localStorage.removeItem('selectionCache');
				deleteCookie('receiptShown');
				paymentInfo.additionalInformation = [];
			} else {
				deleteCookie('giftCardReceiptShown');
			}

			deleteCookie(selectionToUpdate);
			savePaymentInfo(paymentInfo);

			return {
				...state,
				items: {},
				selectionCache: {},
				useShippingAddress: false,
				vouchers: {
					currentValue: '',
					error: false,
				},
				payment: paymentInfo,
				paymentReturn: {},
				addToBagError: false,
				selectionError: false,
				selectionErrorPassword: false,
				selectionErrorRegistered: false,
				selectionErrorStock: false,
				[selectionToUpdate]: {
					token: '',
				},
				destroySelection: false,
				destroyGiftCardSelection: false,
				receiptSnippet: false,
			};
		}

		default:
			return state;
	}
};

export { initialState as checkoutState };
export default checkout;
